import type { IOwnAccountsTransferForm } from "@/modules/transaction/common/types";
import { OwnAccountsTransferForm } from "@/modules/transaction/components";
import {
  ownAccountTransferValidationSchema
} from "@/modules/transaction/validations/validationSchema";
import { DATE_FORMAT } from "@/shared/common/constants";
import {
  CloudflareAsset,
  Currencies,
  SideSheetPosition,
  StaticUrl,
  Storage,
} from "@/shared/common/enums";
import type { IOperationPageProps } from "@/shared/common/types";
import { CloudFlareImage, InfoLinks, PageHeader } from "@/shared/components";
import { formatDate } from "@/shared/helpers";
import { useMediaQuery, useTranslationExists } from "@/shared/hooks";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { FormContainer } from "@ab.uitools/ui-library/components/FormContainer";
import { SideSheet } from "@ab.uitools/ui-library/components/SideSheet";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { useState } from "react";

export const OwnAccountsTransfer = ({ isCopy, isEdit }: IOperationPageProps) => {
  const { t, exists } = useTranslationExists();
  const isDesktop = useMediaQuery();
  const [isSideBarOpen, setSideBarOpen] = useState<boolean>(false);
  const [isNameUnique, setIsNameUnique] = useState<boolean>(false);

  const initialValues: IOwnAccountsTransferForm = {
    transactionNumber: "",
    creditAccount: "",
    debitAccount: "",
    scheduleDate: formatDate(new Date(), DATE_FORMAT.YYYY_MM_DD),
    amount: "",
    tin: localStorage.getItem(Storage.Tin) || "",
    purpose: "",
    currency: Currencies.AMD,
    fee: 0,
    isSaveAsTemplate: false,
    templateName: "",
  };

  return (
    <>
      <FormContainer
        className="full-width page-container form-layout"
        formId="form-container"
        mode="onBlur"
        reValidateMode="onBlur"
        initialValues={initialValues}
        validationScheme={ownAccountTransferValidationSchema({ t, isNameUnique })}
      >
        <PageHeader>
          {!isSideBarOpen && exists("transfer.sidebarTitle") && (
            <Button
              type="secondary"
              size="small"
              className="side-sheet-btn"
              data-testid="open-info-side-sheet"
              data-ga-id="open-info-side-sheet"
              data-ga-action="Open Own account transfer info side sheet"
              iconProps={{
                Component: (props: ISVGIconProps) => (
                  <CloudFlareImage {...props} path={CloudflareAsset.Info}/>
                ),
                alignment: "left",
              }}
              onClick={() => setSideBarOpen(!isSideBarOpen)}
            />
          )}
        </PageHeader>
        <OwnAccountsTransferForm
          isCopy={isCopy}
          isEdit={isEdit}
          isNameUnique={isNameUnique}
          setIsNameUnique={setIsNameUnique}
        />
      </FormContainer>
      <SideSheet
        position={isDesktop ? SideSheetPosition.Right : SideSheetPosition.Bottom}
        isPositioned={!isDesktop}
        withOverlay={!isDesktop}
        isOpen={isSideBarOpen}
        onClose={() => setSideBarOpen(false)}
        title={t("transfer.sidebarTitle")}
      >
        <div className="flexbox flex-wrap--wrap">
          <Text type="primary" className="mb-10">
            {t("transfer.ownAccount.sidebarMessage")}
          </Text>
          <InfoLinks
            keys={[
              StaticUrl.CorporateTariffs,
              StaticUrl.CardTariffs,
              StaticUrl.TermsAndConditions,
            ]}
          />
        </div>
      </SideSheet>
    </>
  );
};
